import React from "react";
import {useHistory} from "react-router";
import {Column} from "react-virtualized";
import {PortfolioCell, PortfolioColumn, PortfolioName} from "./PortfolioName";
import {CurrentRank, CurrentRankColumn, CurrentRankHeader, CurrentRankRow} from "./CurrentRank";
import {Benchmark, KpiBasisCell, KpiCell, KpiCellWrapper, KpiColumn, KpiName} from "./Kpi";
import {VirtualizedTable} from "../../../elements/VirtualizedTable";
import {benchmarkingUsed, performanceValueSlimDescription} from "../../../performance/Kpi";
import {logAction} from "../../../../logging/logEvent";
import {SlimPerformanceValue} from "../../../performance/CompactPerformanceValue";
import KpiCurrency from "../../../performance/KpiCurrency";
import {sortBasedOnState} from "../../../sorting/sort";
import {RightAlignedSortableCell, SortableHeading} from "../../../sorting/SortableHeading";
import {OpenIdeaCountCell, OpenIdeaCountColumn} from "./OpenIdeaCount";
import {PortfolioPerformanceTimestamp} from "./PerformanceTimestamp";


function rankFractionFrom(portfolioRow) {
    if (portfolioRow.rank) {
        return (1.0 * portfolioRow.rank / portfolioRow.totalRanked);
    }
    if (portfolioRow.actionToGetRanked && portfolioRow.actionToGetRanked.eligibility) {
        const required = parseInt(portfolioRow.actionToGetRanked.eligibility);
        if (isNaN(required)) {
            return null;
        }
        return 1.1 * required;
    }
    return null;
}


const ClientsTable = ({userIdentity, allPortfolios, sort, authorActions}) => {
    const history = useHistory();
    const portfolioData = allPortfolios.map(p => { return {...p, rankFraction: rankFractionFrom(p) }});
    const sortedList = sortBasedOnState(portfolioData, sort);

    const rowClassNameFunction = ({index}) => {
        return index === -1 ? "" : "t-portfolio-performance-row";
    };

    function onRowClick({rowData}) {
        const {portfolioId} = rowData;

        logAction({
            type: "PORTFOLIO_AUTHOR_PERFORMANCE_ROW_CLICKED",
            portfolioId: portfolioId
        });

        const url = `/contribute/${portfolioId}/${userIdentity.imeUserId}`;
        history.push(url);
    }

    const PortfolioHeading = (props) => (
        <SortableHeading {...props}
                         sortState={sort}
                         tableName="homepage_allClients"
                         sortByFieldFn={authorActions.sortPortfoliosBy}/>
    );

    return <VirtualizedTable headerHeight={45}
                             rowHeight={64}
                             bordered={true}
                             onRowClick={onRowClick}
                             rowCount={sortedList.length}
                             rowClassName={rowClassNameFunction}
                             rowGetter={({index}) => sortedList[index]}>

        <Column dataKey="name"
                minWidth={94}
                width={94}
                flexShrink={0}
                flexGrow={1}
                headerRenderer={() => {
                    return (<PortfolioHeading fieldName="portfolioName"
                                              descendingFirst={false}>
                        <PortfolioColumn>
                            Portfolio
                        </PortfolioColumn>
                    </PortfolioHeading>);
                }}
                cellRenderer={({rowData}) => {
                    const {portfolioName, portfolioCompanyName, portfolioMessage} = rowData;
                    return <PortfolioCell className="t-portfolio-name">
                        <PortfolioName portfolioName={portfolioName} portfolioCompanyName={portfolioCompanyName}
                                       portfolioMessage={portfolioMessage}/>
                    </PortfolioCell>
                }}/>

        <Column dataKey="openIdeaCount"
                width={40}
                flexShrink={0}
                flexGrow={0}
                headerRenderer={() => {
                    return (<PortfolioHeading fieldName="openIdeaCount"
                                              descendingFirst={false}
                                              horizontalAlignment="flex-end">
                        <OpenIdeaCountColumn>
                            <div>Open</div>
                            <div>Ideas</div>
                        </OpenIdeaCountColumn>
                    </PortfolioHeading>);
                }}
                cellRenderer={({rowData}) => {
                    return <RightAlignedSortableCell>
                        <OpenIdeaCountCell className="t-open-idea-count">
                            {rowData.openIdeaCount}
                        </OpenIdeaCountCell>
                    </RightAlignedSortableCell>
                }}/>

        <Column dataKey="kpi"
                width={60}
                flexShrink={0}
                flexGrow={0}
                headerRenderer={() => {
                    return <KpiColumn>
                            <div>KPI</div>
                        </KpiColumn>;
                }}
                cellRenderer={({rowData}) => {
                    const {kpi, kpiValue, currency} = rowData;
                    if (!kpi) {
                        return <KpiCellWrapper>&mdash;</KpiCellWrapper>
                    }

                    return <KpiCellWrapper>
                        <KpiCell>
                            <SlimPerformanceValue className="t-kpi-value" kpi={kpi} value={kpiValue}/>
                            <KpiCurrency className="t-kpi-currency" kpi={kpi} value={kpiValue} currency={currency}/>
                        </KpiCell>
                    </KpiCellWrapper>;
                }}/>

        <Column dataKey="kpiBasis"
                width={64}
                flexShrink={0}
                flexGrow={0}
                headerRenderer={() => <div/>}
                cellRenderer={({rowData}) => {
                    const {kpi, benchmarkSource, defaultBenchmark} = rowData;
                    return <KpiBasisCell>
                        <KpiName className="t-kpi-name">{performanceValueSlimDescription(kpi)}</KpiName>
                        <Benchmark className="t-kpi-benchmark">{benchmarkingUsed(kpi, benchmarkSource, defaultBenchmark)}</Benchmark>
                    </KpiBasisCell>;
                }}/>

        <Column dataKey="rankFraction"
                width={100}
                flexShrink={0}
                flexGrow={0}
                headerRenderer={() => {
                    return (<CurrentRankColumn>
                        <PortfolioHeading fieldName="rankFraction"
                                          descendingFirst={false}
                                          horizontalAlignment="flex-end">
                            <CurrentRankHeader>
                                <div>Current</div>
                                <div>Rank</div>
                            </CurrentRankHeader>
                        </PortfolioHeading>
                    </CurrentRankColumn>);
                }}
                cellRenderer={({rowData}) => {
                    const {kpi, rank, totalRanked, actionToGetRanked} = rowData;
                    return (<RightAlignedSortableCell>
                        <CurrentRankRow>
                            <CurrentRank kpi={kpi}
                                         rank={{rank, totalRanked}}
                                         actionToGetRanked={actionToGetRanked}/>
                            <PortfolioPerformanceTimestamp generatedAt={rowData.generatedAt}/>
                        </CurrentRankRow>
                    </RightAlignedSortableCell>);
                }}/>

    </VirtualizedTable>;
};

export default ClientsTable;
